import { z } from 'zod';

import { fillSessionPlaceholders, pickRemembered } from '../api/docs-session';
import { isManagedHeader } from '../api/try-it-client';
import type { DocsOperation, DocsParam } from '../generated/reference';

import type { DocsSession } from './docs-storage-schema';

/** Values by parameter name per location, the body as JSON text, and the Idempotency-Key. */
export interface TryItValues {
  path: Record<string, string>;
  query: Record<string, string>;
  header: Record<string, string>;
  body: string;
  idempotencyKey: string;
}

const REQUIRED = 'docs:tryIt.validation.required';
const INVALID_JSON = 'docs:tryIt.validation.json';

export function formParams(operation: DocsOperation, where: DocsParam['in']): DocsParam[] {
  return operation.parameters.filter(
    (p) => p.in === where && !(where === 'header' && isManagedHeader(p.name)),
  );
}

/** Path params are always required; the body must parse as JSON when present. */
export function tryItFormSchema(operation: DocsOperation) {
  const group = (where: DocsParam['in']) =>
    z.object(
      Object.fromEntries(
        formParams(operation, where).map((p) => [
          p.name,
          p.required || where === 'path'
            ? z.string().trim().min(1, { error: REQUIRED })
            : z.string(),
        ]),
      ),
    );
  const bodyRequired = operation.requestBody?.required ?? false;
  return z.object({
    path: group('path'),
    query: group('query'),
    header: group('header'),
    body: z.string().superRefine((text, ctx) => {
      if (!operation.requestBody) return;
      if (!text.trim()) {
        if (bodyRequired) ctx.addIssue({ code: 'custom', message: REQUIRED });
        return;
      }
      try {
        JSON.parse(text);
      } catch {
        ctx.addIssue({ code: 'custom', message: INVALID_JSON });
      }
    }),
    idempotencyKey: z.string(),
  }) satisfies z.ZodType<TryItValues, TryItValues>;
}

/**
 * Recorded ids and tokens belong to the organisation the examples were recorded in, which no
 * longer exists, so they are placeholders only; handles and names are worth prefilling.
 */
const isRecordedOnly = (name: string) => /(^id$|Id$|[tT]oken$)/.test(name);

/** A parameter example as input text. */
export function exampleText(value: unknown): string {
  if (value === undefined || value === null) return '';
  if (typeof value === 'string') return value;
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  return JSON.stringify(value);
}

/** The recorded request body, pretty-printed, with the customer's tokens filled in. */
export function exampleBody(operation: DocsOperation, session: DocsSession): string {
  if (!operation.requestBody) return '';
  const recorded = operation.examples.request?.body;
  if (recorded === undefined) return '{}';
  return fillSessionPlaceholders(JSON.stringify(recorded, null, 2), session);
}

export interface TryItDefaults {
  values: TryItValues;
  /** Path param name → operation id its remembered value came from. */
  sources: Record<string, string>;
}

/**
 * Starting values: a path param takes the value remembered from an earlier response (plan 9.2),
 * else its example unless that is a recorded id or token; a required query param takes its
 * example; the body is the recorded one; a fresh Idempotency-Key.
 */
export function tryItDefaults(operation: DocsOperation, session: DocsSession): TryItDefaults {
  const sources: Record<string, string> = {};
  const path = Object.fromEntries(
    formParams(operation, 'path').map((p) => {
      const remembered = pickRemembered(session, operation, p.name);
      if (remembered) {
        sources[p.name] = remembered.from;
        return [p.name, remembered.value];
      }
      return [p.name, isRecordedOnly(p.name) ? '' : exampleText(p.example)];
    }),
  );
  const query = Object.fromEntries(
    formParams(operation, 'query').map((p) => [p.name, p.required ? exampleText(p.example) : '']),
  );
  const header = Object.fromEntries(formParams(operation, 'header').map((p) => [p.name, '']));
  return {
    values: {
      path,
      query,
      header,
      body: exampleBody(operation, session),
      idempotencyKey: crypto.randomUUID(),
    },
    sources,
  };
}
